export const MARYLAND_LOCALITY_VALUES = [
  'allegany',
  'anne_arundel',
  'baltimore_city',
  'baltimore_county',
  'calvert',
  'caroline',
  'carroll',
  'cecil',
  'charles',
  'dorchester',
  'frederick',
  'garrett',
  'harford',
  'howard',
  'kent',
  'montgomery',
  'prince_georges',
  'queen_annes',
  'st_marys',
  'somerset',
  'talbot',
  'washington',
  'wicomico',
  'worcester',
] as const;

export type MarylandLocalityCode = (typeof MARYLAND_LOCALITY_VALUES)[number];

export interface MarylandLocalityOption {
  value: MarylandLocalityCode;
  label: string;
}

export const MARYLAND_LOCALITY_OPTIONS: MarylandLocalityOption[] = [
  { value: 'allegany', label: 'Allegany County' },
  { value: 'anne_arundel', label: 'Anne Arundel County' },
  { value: 'baltimore_city', label: 'Baltimore City' },
  { value: 'baltimore_county', label: 'Baltimore County' },
  { value: 'calvert', label: 'Calvert County' },
  { value: 'caroline', label: 'Caroline County' },
  { value: 'carroll', label: 'Carroll County' },
  { value: 'cecil', label: 'Cecil County' },
  { value: 'charles', label: 'Charles County' },
  { value: 'dorchester', label: 'Dorchester County' },
  { value: 'frederick', label: 'Frederick County' },
  { value: 'garrett', label: 'Garrett County' },
  { value: 'harford', label: 'Harford County' },
  { value: 'howard', label: 'Howard County' },
  { value: 'kent', label: 'Kent County' },
  { value: 'montgomery', label: 'Montgomery County' },
  { value: 'prince_georges', label: "Prince George's County" },
  { value: 'queen_annes', label: "Queen Anne's County" },
  { value: 'st_marys', label: "St. Mary's County" },
  { value: 'somerset', label: 'Somerset County' },
  { value: 'talbot', label: 'Talbot County' },
  { value: 'washington', label: 'Washington County' },
  { value: 'wicomico', label: 'Wicomico County' },
  { value: 'worcester', label: 'Worcester County' },
];

const FLAT_MARYLAND_LOCAL_RATES: Record<MarylandLocalityCode, number> = {
  allegany: 0.0303,
  anne_arundel: 0,
  baltimore_city: 0.032,
  baltimore_county: 0.032,
  calvert: 0.032,
  caroline: 0.032,
  carroll: 0.0303,
  cecil: 0.0274,
  charles: 0.0303,
  dorchester: 0.032,
  frederick: 0,
  garrett: 0.0265,
  harford: 0.0306,
  howard: 0.032,
  kent: 0.032,
  montgomery: 0.032,
  prince_georges: 0.032,
  queen_annes: 0.032,
  st_marys: 0.032,
  somerset: 0.032,
  talbot: 0.024,
  washington: 0.0295,
  wicomico: 0.032,
  worcester: 0.0225,
};

function anneArundelRate(
  taxableIncome: number,
  filingStatus: 'single' | 'married_filing_jointly',
): number {
  // Anne Arundel is marginal; callers need an effective rate to multiply by income.
  const first = filingStatus === 'married_filing_jointly' ? 75000 : 50000;
  const second = filingStatus === 'married_filing_jointly' ? 480000 : 400000;
  if (taxableIncome <= 0) return 0.027;
  const tax = Math.min(taxableIncome, first) * 0.027
    + Math.max(0, Math.min(taxableIncome, second) - first) * 0.0294
    + Math.max(0, taxableIncome - second) * 0.032;
  return tax / taxableIncome;
}

function frederickRate(
  taxableIncome: number,
  filingStatus: 'single' | 'married_filing_jointly',
): number {
  if (filingStatus === 'married_filing_jointly') {
    if (taxableIncome <= 25_000) return 0.0225;
    if (taxableIncome <= 100_000) return 0.0275;
    if (taxableIncome <= 250_000) return 0.0296;
    return 0.032;
  }

  if (taxableIncome <= 25_000) return 0.0225;
  if (taxableIncome <= 50_000) return 0.0275;
  if (taxableIncome <= 150_000) return 0.0296;
  return 0.032;
}

export function getMarylandLocalityLabel(
  locality: string | null | undefined,
): string | null {
  if (!locality) return null;
  return (
    MARYLAND_LOCALITY_OPTIONS.find((option) => option.value === locality)?.label ??
    null
  );
}

export function isMarylandLocalityCode(
  locality: string | null | undefined,
): locality is MarylandLocalityCode {
  if (!locality) return false;
  return (MARYLAND_LOCALITY_VALUES as readonly string[]).includes(locality);
}

export function getMarylandLocalTaxRate(args: {
  locality: string | null | undefined;
  year?: number;
  taxableIncome: number;
  filingStatus: 'single' | 'married_filing_jointly';
}): number | null {
  const { locality, taxableIncome, filingStatus } = args;
  if (!locality) return null;
  // Maryland Comptroller 2025 BRFA alert, revised December 22, 2025.
  if ((args.year ?? 2025) >= 2026 && locality === 'allegany') return 0.032;
  if ((args.year ?? 2025) >= 2026 && locality === 'kent') return 0.033;
  if ((args.year ?? 2025) >= 2025 && locality === 'dorchester') return 0.033;

  if (locality === 'anne_arundel') {
    return anneArundelRate(taxableIncome, filingStatus);
  }

  if (locality === 'frederick') {
    return frederickRate(taxableIncome, filingStatus);
  }

  return FLAT_MARYLAND_LOCAL_RATES[locality as MarylandLocalityCode] ?? null;
}
